/**
 * База API. В dev — пустая строка (Vite проксирует /api на локальный Worker).
 * В проде задаётся через VITE_API_BASE (URL Worker) на этапе сборки Pages.
 */
const API_BASE = import.meta.env.VITE_API_BASE ?? '';

const TOKEN_KEY = 'azam_token';

// Кэш условных запросов: ETag + тело последнего успешного ответа по каждому пути.
// Живёт только в памяти вкладки. Очищается при смене пользователя — иначе новый
// пользователь мог бы получить 304 и увидеть данные предыдущего.
const etagCache = new Map<string, { etag: string; body: unknown }>();
export const clearApiCache = (): void => etagCache.clear();

export const getToken = (): string | null => localStorage.getItem(TOKEN_KEY);
export const setToken = (t: string | null): void => {
  clearApiCache(); // вход/выход/смена роли — чужой кэш недействителен
  if (t) localStorage.setItem(TOKEN_KEY, t);
  else localStorage.removeItem(TOKEN_KEY);
};

function authHeaders(): Record<string, string> {
  const t = getToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

/**
 * GET с условным запросом (ETag). Сводки опрашиваются по таймеру, и если на сервере
 * ничего не менялось — приходит 304 без тела, а D1 читает 1 строку вместо всей сводки.
 *
 * If-None-Match шлём ТОЛЬКО когда есть сохранённое тело: иначе на 304 нечего было бы
 * вернуть. Если 304 всё же пришёл без кэша — уходим в обычную ошибку, вызывающий
 * оставит прежние данные, а следующий тик (уже без заголовка) вернёт полный ответ.
 */
export async function apiGet<T>(path: string): Promise<T> {
  const cached = etagCache.get(path);
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { ...authHeaders(), ...(cached ? { 'If-None-Match': cached.etag } : {}) },
  });
  if (res.status === 304 && cached) return cached.body as T;
  if (!res.ok) throw new Error(`API ${path} → ${res.status}`);
  const body = (await res.json()) as T;
  const etag = res.headers.get('ETag');
  if (etag) etagCache.set(path, { etag, body });
  else etagCache.delete(path); // сервер перестал давать ETag — не держим устаревшее
  return body;
}

export async function apiPost<T>(path: string, body?: unknown): Promise<T> {
  return apiSend<T>('POST', path, body);
}

// Универсальный запрос с телом (POST/PUT/PATCH/DELETE). Читает и message, и error.
export async function apiSend<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
    throw new Error(err.message ?? err.error ?? `API ${path} → ${res.status}`);
  }
  return res.json().catch(() => ({})) as Promise<T>;
}
